<template>
  <section class="tw-relative tw-container tw-mx-auto tw-px-6 tw-py-20 md:tw-py-32 tw-overflow-hidden">
    <!-- Background decorative elements -->
    <div class="tw-absolute tw-top-0 tw-right-0 tw-w-96 tw-h-96 tw-bg-primary-100/20 tw-rounded-full tw-blur-3xl tw--translate-y-1/2 tw-translate-x-1/2"></div>
    <div class="tw-absolute tw-bottom-0 tw-left-0 tw-w-80 tw-h-80 tw-bg-blue-100/20 tw-rounded-full tw-blur-3xl tw-translate-y-1/2 tw--translate-x-1/2"></div>
    
    <div class="tw-relative tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-16 tw-items-center">
      <!-- Left Column -->
      <div class="tw-space-y-6">
        <!-- Badge -->
        <div class="tw-inline-flex tw-items-center tw-gap-2 tw-bg-primary-50 tw-text-primary-700 tw-px-4 tw-py-2 tw-rounded-full tw-text-sm tw-font-semibold tw-mb-2">
          <svg class="tw-w-4 tw-h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
          <span>Built for modern B2B event teams</span>
        </div>
        
        <h1 class="tw-text-5xl md:tw-text-6xl lg:tw-text-7xl tw-font-bold tw-text-gray-900 tw-leading-tight">
          Run B2B Trade Shows That Generate <span class="tw-text-primary-600">Real Deals</span>
        </h1>
        <p class="tw-text-xl tw-text-gray-600 tw-leading-relaxed">
          Intelligent matchmaking platform that turns attendees into business relationships.
        </p>
        
        <!-- Stats -->
        <div class="tw-flex tw-items-center tw-gap-8 tw-pt-2">
          <div>
            <div class="tw-text-2xl tw-font-bold tw-text-gray-900">3X</div>
            <div class="tw-text-sm tw-text-gray-600">More Meetings</div>
          </div>
          <div class="tw-w-px tw-h-12 tw-bg-gray-200"></div>
          <div>
            <div class="tw-text-2xl tw-font-bold tw-text-gray-900">89%</div>
            <div class="tw-text-sm tw-text-gray-600">Completion Rate</div>
          </div>
          <div class="tw-w-px tw-h-12 tw-bg-gray-200"></div>
          <div>
            <div class="tw-text-2xl tw-font-bold tw-text-gray-900">40%</div>
            <div class="tw-text-sm tw-text-gray-600">Higher Satisfaction</div>
          </div>
        </div>
        
        <div class="tw-flex tw-flex-col sm:tw-flex-row tw-gap-4 tw-pt-4">
          <a
            :href="appConfig.calendlyUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="tw-bg-primary-600 tw-text-white tw-px-8 tw-py-4 tw-rounded-xl tw-text-base tw-font-semibold hover:tw-bg-primary-700 tw-transition-all tw-shadow-md hover:tw-shadow-lg tw-transform hover:tw-scale-105 tw-inline-block tw-text-center"
          >
            Schedule a Demo
          </a>
          <RouterLink
            :to="{ name: 'home', hash: '#how-it-works' }"
            class="tw-text-primary-600 tw-px-8 tw-py-4 tw-rounded-xl tw-text-base tw-font-semibold hover:tw-text-primary-700 tw-transition-colors tw-flex tw-items-center tw-justify-center tw-border tw-border-primary-200 hover:tw-border-primary-300"
          >
            See How It Works →
          </RouterLink>
        </div>
      </div>
      
      <!-- Right Column - Hero Visual with Illustrations -->
      <div class="tw-relative">
        <!-- Main Image -->
        <div class="tw-relative tw-rounded-3xl tw-shadow-xl tw-h-[500px] tw-overflow-hidden tw-border tw-border-gray-100 tw-bg-gray-50 tw-z-10">
          <img 
            src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1000&h=700&fit=crop&q=90" 
            alt="B2B Matchmaking Dashboard"
            class="tw-w-full tw-h-full tw-object-cover"
            loading="lazy"
          />
        </div>
        
        <!-- Floating Illustration Cards -->
        <div class="tw-absolute tw-top-4 tw-right-4 tw-bg-white tw-p-4 tw-rounded-xl tw-shadow-lg tw-border tw-border-gray-100 tw-z-20">
          <div class="tw-flex tw-items-center tw-gap-3">
            <div class="tw-w-12 tw-h-12 tw-bg-green-100 tw-rounded-full tw-flex tw-items-center tw-justify-center">
              <svg class="tw-w-6 tw-h-6 tw-text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <div>
              <div class="tw-text-sm tw-font-bold tw-text-gray-900">3,400+</div>
              <div class="tw-text-xs tw-text-gray-600">Meetings</div>
            </div>
          </div>
        </div>
        
        <div class="tw-absolute tw-bottom-12 tw--left-8 tw-bg-white tw-p-4 tw-rounded-xl tw-shadow-lg tw-border tw-border-gray-100 tw-z-20">
          <div class="tw-flex tw-items-center tw-gap-3">
            <div class="tw-w-12 tw-h-12 tw-bg-primary-100 tw-rounded-full tw-flex tw-items-center tw-justify-center">
              <svg class="tw-w-6 tw-h-6 tw-text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <div>
              <div class="tw-text-sm tw-font-bold tw-text-gray-900">89%</div>
              <div class="tw-text-xs tw-text-gray-600">Completion</div>
            </div>
          </div>
        </div>
        
        <!-- Decorative dots -->
        <div class="tw-absolute tw-top-1/2 tw-right-0 tw-w-20 tw-h-20 tw-transform tw-translate-x-1/2 tw--translate-y-1/2 tw-z-0">
          <div class="tw-w-full tw-h-full tw-relative">
            <div class="tw-absolute tw-w-3 tw-h-3 tw-bg-primary-400 tw-rounded-full tw-top-0 tw-left-1/2"></div>
            <div class="tw-absolute tw-w-2 tw-h-2 tw-bg-primary-300 tw-rounded-full tw-bottom-0 tw-left-0"></div>
            <div class="tw-absolute tw-w-2 tw-h-2 tw-bg-primary-300 tw-rounded-full tw-bottom-0 tw-right-0"></div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import appConfig from '../config/app.config.js';
</script>
